import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Database, Palette, UserPlus, BarChart3, FileText, Check, Plus, MessageCircle, Globe } from 'lucide-react';

const pulseGlow = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.05);
  }
`;

const Section = styled.section`
  padding: 120px 0;
  background: #ffffff;
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -20%;
    right: -10%;
    width: 80%;
    height: 120%;
    background: 
      radial-gradient(ellipse 60% 80% at 20% 30%, rgba(138, 43, 226, 0.08) 0%, transparent 50%),
      radial-gradient(ellipse 70% 60% at 80% 70%, rgba(59, 130, 246, 0.08) 0%, transparent 55%);
    filter: blur(80px);
    opacity: 0.6;
    z-index: 0;
    animation: ${pulseGlow} 10s ease-in-out infinite;
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 60px 0;
  }

  @media (max-width: 480px) {
    padding: 40px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 80px;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  @media (max-width: 480px) {
    padding: 0 16px;
    gap: 32px;
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 42px;
  font-weight: 700;
  color: #0b0f1a;
  margin-bottom: 16px;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 32px;
  }

  @media (max-width: 480px) {
    font-size: 26px;
    margin-bottom: 12px;
  }
`;

const Subtitle = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  color: #6b7280;
  line-height: 1.6;

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 1.5;
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FeatureItem = styled(motion.div) <{ isActive: boolean }>`
  padding: 24px;
  border-radius: 20px;
  cursor: pointer;
  background: ${props => props.isActive ? '#ffffff' : 'transparent'};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid ${props => props.isActive ? 'rgba(138, 43, 226, 0.1)' : 'transparent'};
  position: relative;
  overflow: hidden;
  box-shadow: ${props => props.isActive ? '0 20px 40px -10px rgba(138, 43, 226, 0.1)' : 'none'};

  &:hover {
    background: ${props => props.isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'};
  }

  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 16px;
  }
`;

const ProgressBar = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(90deg, #8b5cf6, #3b82f6);
  width: 100%;
  transform-origin: left;
`;

const FeatureHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const FeatureTitle = styled.h3<{ isActive: boolean }>`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.isActive ? '#0b0f1a' : '#4b5563'};
  margin: 0;

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const FeatureDescription = styled.p<{ isActive: boolean }>`
  font-size: 15px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
  display: ${props => props.isActive ? 'block' : 'none'};
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const VisualContainer = styled.div`
  position: relative;
  height: 640px;
  background: linear-gradient(135deg, rgba(138, 43, 226, 0.15), rgba(59, 130, 246, 0.15));
  backdrop-filter: blur(20px);
  border-radius: 32px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  overflow: hidden;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.5) inset;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(138, 43, 226, 0.05), rgba(59, 130, 246, 0.05));
    z-index: -1;
  }

  @media (max-width: 968px) {
    height: 500px;
  }

  @media (max-width: 768px) {
    height: 380px;
    border-radius: 24px;
  }

  @media (max-width: 480px) {
    height: 320px;
    border-radius: 20px;
  }

  @media (max-width: 380px) {
    height: 280px;
    border-radius: 16px;
  }
`;

const VisualContent = styled(motion.div)`
  width: 100%;
  height: 100%;
  padding: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 20px;
    /* Scale down the entire visual for tablet */
    transform: scale(0.85);
    transform-origin: center center;
  }

  @media (max-width: 480px) {
    padding: 8px;
    /* Scale down more for mobile */
    transform: scale(0.65);
    transform-origin: center center;
  }

  @media (max-width: 380px) {
    transform: scale(0.55);
  }
`;

// --- Visual Components based on Actual Product ---

// 1. Train on your data (Motion Graphic)
const SCENARIOS = [
    {
        type: 'PDF',
        icon: FileText,
        color: '#ef4444',
        bg: '#fef2f2',
        source: 'Return_Policy.pdf',
        query: "What's your return policy?",
        answer: "You can return items within 30 days for a full refund."
    },
    {
        type: 'URL',
        icon: Globe,
        color: '#3b82f6',
        bg: '#eff6ff',
        source: 'shop.com/dinner-sets',
        query: "Help me find a dinner set within my budget",
        answer: "I found 3 dinner sets under $50. The 'Classic White' set is a top pick."
    },
    {
        type: 'Q&A',
        icon: MessageCircle,
        color: '#f59e0b',
        bg: '#fffbeb',
        source: 'General Q&A',
        query: "How do I contact you?",
        answer: "You can reach SmushLabs customer support at support@smushlabs.ai."
    }
];

const FilesVisual = () => {
    const [scenarioIndex, setScenarioIndex] = useState(0);
    const [step, setStep] = useState(0); // 0: Source, 1: Query, 2: Answer

    useEffect(() => {
        const sequence = async () => {
            while (true) {
                for (let i = 0; i < SCENARIOS.length; i++) {
                    setScenarioIndex(i);
                    setStep(0); // Tab Appears
                    await new Promise(r => setTimeout(r, 500));

                    setStep(1); // Cursor Move
                    await new Promise(r => setTimeout(r, 1000));

                    setStep(2); // Cursor Click
                    await new Promise(r => setTimeout(r, 300));

                    setStep(3); // Source Appears
                    await new Promise(r => setTimeout(r, 1000));

                    setStep(4); // Query Appears
                    await new Promise(r => setTimeout(r, 1500));

                    setStep(5); // Answer Appears
                    await new Promise(r => setTimeout(r, 3500));

                    setStep(-1); // Transition Out
                    await new Promise(r => setTimeout(r, 500));
                }
            }
        };
        sequence();
    }, []);

    const current = SCENARIOS[scenarioIndex];
    const Icon = current.icon;

    return (
        <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', borderRadius: '12px', border: '1px solid #cbd5e1', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>

            {/* Background Grid */}
            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '20px 20px', opacity: 0.5 }} />

            <div style={{ position: 'relative', width: '100%', maxWidth: '400px', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '140px' }}>

                {/* Connecting Lines (SVG) */}
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}>
                    <AnimatePresence mode="wait">
                        {step >= 4 && (
                            <motion.path
                                key={`line-1-${scenarioIndex}`}
                                initial={{ pathLength: 0, opacity: 0 }}
                                animate={{ pathLength: 1, opacity: 1 }}
                                exit={{ opacity: 0 }}
                                d="M 100 150 C 150 150 150 80 220 80" // Curve to User Bubble
                                fill="none"
                                stroke={current.color}
                                strokeWidth="2"
                                strokeDasharray="6 6"
                                strokeLinecap="round"
                            />
                        )}
                    </AnimatePresence>
                    <AnimatePresence mode="wait">
                        {step >= 5 && (
                            <motion.path
                                key={`line-2-${scenarioIndex}`}
                                initial={{ pathLength: 0, opacity: 0 }}
                                animate={{ pathLength: 1, opacity: 1 }}
                                exit={{ opacity: 0 }}
                                d="M 100 150 C 150 150 150 220 220 220" // Curve to Assistant Bubble
                                fill="none"
                                stroke={current.color}
                                strokeWidth="2"
                                strokeDasharray="6 6"
                                strokeLinecap="round"
                            />
                        )}
                    </AnimatePresence>
                </svg>

                {/* Moving Particles on Lines */}
                {step >= 4 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2 }}
                    >
                        <svg style={{ width: '100%', height: '100%' }}>
                            <motion.path
                                d="M 100 150 C 150 150 150 80 220 80"
                                fill="none"
                                stroke={current.color}
                                strokeWidth="2"
                                strokeDasharray="6 6"
                                animate={{ strokeDashoffset: [0, -12] }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            />
                        </svg>
                    </motion.div>
                )}
                {step >= 5 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2 }}
                    >
                        <svg style={{ width: '100%', height: '100%' }}>
                            <motion.path
                                d="M 100 150 C 150 150 150 220 220 220"
                                fill="none"
                                stroke={current.color}
                                strokeWidth="2"
                                strokeDasharray="6 6"
                                animate={{ strokeDashoffset: [0, -12] }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            />
                        </svg>
                    </motion.div>
                )}


                {/* Source Card (Left) */}
                <AnimatePresence mode="wait">
                    {step >= 3 && (
                        <motion.div
                            key={`source-${scenarioIndex}`}
                            initial={{ scale: 0.8, opacity: 0, x: -20 }}
                            animate={{ scale: 1, opacity: 1, x: 0 }}
                            exit={{ scale: 0.8, opacity: 0, x: -20 }}
                            style={{
                                position: 'absolute', left: '40px', top: '110px', // Centered vertically around 150
                                width: '80px', height: '80px',
                                background: 'white', borderRadius: '12px',
                                border: `1px solid ${current.color}`,
                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                zIndex: 10
                            }}
                        >
                            <div style={{ padding: '8px', borderRadius: '50%', background: current.bg }}>
                                <Icon size={24} color={current.color} />
                            </div>
                            <span style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', maxWidth: '90%', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {current.type}
                            </span>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* User Bubble (Top Right) */}
                <AnimatePresence mode="wait">
                    {step >= 4 && (
                        <motion.div
                            key={`query-${scenarioIndex}`}
                            initial={{ opacity: 0, y: 10, x: 10 }}
                            animate={{ opacity: 1, y: 0, x: 0 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            style={{
                                position: 'absolute', right: '20px', top: '40px',
                                maxWidth: '180px', padding: '12px',
                                background: 'white', borderRadius: '12px 12px 0 12px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                border: '1px solid #e2e8f0',
                                zIndex: 10
                            }}
                        >
                            <p style={{ fontSize: '12px', color: '#1e293b', margin: 0, lineHeight: 1.4 }}>{current.query}</p>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Assistant Bubble (Bottom Right) */}
                <AnimatePresence mode="wait">
                    {step >= 5 && (
                        <motion.div
                            key={`answer-${scenarioIndex}`}
                            initial={{ opacity: 0, y: 10, x: 10 }}
                            animate={{ opacity: 1, y: 0, x: 0 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            style={{
                                position: 'absolute', right: '20px', bottom: '40px',
                                maxWidth: '180px',
                                zIndex: 10
                            }}
                        >
                            {/* Magical Glow Container */}
                            <div style={{
                                position: 'absolute',
                                inset: '-3px', // Slightly larger for the border effect
                                borderRadius: '14px 14px 14px 2px', // Matching bubble radius + padding
                                overflow: 'hidden',
                                zIndex: -1
                            }}>
                                {/* Rotating Beam */}
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                                    style={{
                                        position: 'absolute',
                                        top: '50%', left: '50%',
                                        width: '500px', height: '500px',
                                        marginLeft: '-250px', marginTop: '-250px',
                                        background: 'conic-gradient(from 0deg, transparent 0deg, transparent 270deg, rgba(138, 43, 226, 0.8) 320deg, rgba(59, 130, 246, 0.8) 360deg)',
                                        filter: 'blur(12px)',
                                    }}
                                />
                            </div>

                            {/* Bubble Content */}
                            <div style={{
                                background: 'white',
                                padding: '12px',
                                borderRadius: '12px 12px 12px 0',
                                border: '1px solid rgba(138, 43, 226, 0.1)', // Subtle inner border
                                boxShadow: '0 4px 15px rgba(138, 43, 226, 0.1)'
                            }}>
                                <p style={{ fontSize: '12px', color: '#1e293b', margin: 0, lineHeight: 1.4 }}>{current.answer}</p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Add Button & Menu */}
            <div style={{ position: 'absolute', bottom: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', zIndex: 20 }}>
                <AnimatePresence mode="wait">
                    {step !== -1 && (
                        <motion.div
                            key={scenarioIndex}
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            style={{ background: 'white', padding: '8px', borderRadius: '12px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', border: '1px solid #e2e8f0', display: 'flex', gap: '8px' }}
                        >
                            {SCENARIOS.map((s, i) => (
                                <div
                                    key={i}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        background: i === scenarioIndex && step >= 2 ? '#eff6ff' : 'transparent',
                                        color: i === scenarioIndex && step >= 2 ? '#3b82f6' : '#64748b',
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <s.icon size={16} /> {s.type}
                                </div>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>

                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{ background: '#1e293b', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '30px', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', cursor: 'pointer' }}
                >
                    <Plus size={18} /> Add Source
                </motion.button>
            </div>

            {/* Simulated Cursor */}
            <motion.div
                animate={{
                    x: step === 1 || step === 2 ? (scenarioIndex === 0 ? -90 : scenarioIndex === 1 ? 0 : 90) : 0,
                    y: step === 0 ? 100 : step === 1 || step === 2 ? 0 : 100,
                    opacity: step === 1 || step === 2 ? 1 : 0,
                    scale: step === 2 ? 0.9 : 1
                }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                style={{ position: 'absolute', bottom: '85px', left: '50%', pointerEvents: 'none', zIndex: 50 }}
            >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3L10.07 19.97L12.58 12.58L19.97 10.07L3 3Z" fill="#1e293b" stroke="white" strokeWidth="2" strokeLinejoin="round" />
                </svg>
            </motion.div>

        </div>
    );
};

// 2. Customizable Appearance (Horizontal Story)

const LAUNCHERS = [1, 15, 30, 45, 60].map(i => `/assets/Avatars/AV${i}.png`);

const NAMES = ["Lyra AI", "Jade's Assistant", "Support Bot", "Help Desk"];

const HEADER_STYLES = [
    { bg: 'transparent', border: true }, // Wireframe base
    { bg: '#3b82f6', border: false },    // Blue
    { bg: '#10b981', border: false },    // Green
    { bg: '#1e293b', border: false }     // Dark
];

const FULL_THEMES = [
    { name: "Lyra AI", color: "#7c3aed", msg: "How can I help you today?" },
    { name: "Josh's Garage", color: "#f59e0b", msg: "Welcome to Josh's Garage!" },
    { name: "Dr. Smith", color: "#0ea5e9", msg: "Dr. Smith is online." }
];

const CustomizationVisual = () => {
    const [stage, setStage] = useState(0);

    // Cycle through stages
    useEffect(() => {
        const interval = setInterval(() => {
            setStage((prev) => (prev + 1) % 4);
        }, 3500); // 3.5s per stage
        return () => clearInterval(interval);
    }, []);

    // Stage 1: Launchers (Avatars)
    // Uses global LAUNCHERS

    // Stage 2: Identity (Names)
    const [nameIndex, setNameIndex] = useState(0);

    // Stage 3: Styling (Headers)
    const [styleIndex, setStyleIndex] = useState(0);

    // Stage 4: Full Experience
    const [themeIndex, setThemeIndex] = useState(0);

    // Sub-animations for text/styles within stages
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (stage === 1) {
            interval = setInterval(() => setNameIndex(p => (p + 1) % NAMES.length), 800);
        } else if (stage === 2) {
            interval = setInterval(() => setStyleIndex(p => (p + 1) % HEADER_STYLES.length), 800);
        } else if (stage === 3) {
            interval = setInterval(() => setThemeIndex(p => (p + 1) % FULL_THEMES.length), 1000);
        }
        return () => clearInterval(interval);
    }, [stage]);

    return (
        <div style={{ width: '100%', height: '100%', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', position: 'relative' }}>

            {/* Background Grid */}
            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '20px 20px', opacity: 0.4 }} />

            {/* Sliding Track */}
            <motion.div
                animate={{ x: `-${stage * 25}%` }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
                style={{ display: 'flex', width: '400%', height: '100%' }}
            >
                {/* STAGE 1: Launchers */}
                <div style={{ width: '25%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '24px', position: 'relative' }}>
                    <div style={{ display: 'flex', gap: '20px' }}>
                        {LAUNCHERS.map((src, i) => (
                            <motion.div
                                key={i}
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: i * 0.1 }}
                                style={{
                                    width: '60px', height: '60px',
                                    borderRadius: '50%',
                                    border: '2px solid white',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                    overflow: 'hidden',
                                    cursor: 'pointer'
                                }}
                            >
                                <img src={src} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </motion.div>
                        ))}
                    </div>
                    <div style={{ padding: '8px 16px', background: 'white', borderRadius: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', fontSize: '14px', fontWeight: 600, color: '#64748b' }}>
                        Fits any industry
                    </div>
                </div>

                {/* STAGE 2: Identity (Wireframe) */}
                <div style={{ width: '25%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '24px' }}>
                    <div style={{
                        width: '300px', height: '400px',
                        border: '2px solid #94a3b8', borderRadius: '12px',
                        display: 'flex', flexDirection: 'column',
                        background: 'rgba(255,255,255,0.5)'
                    }}>
                        {/* Wireframe Header */}
                        <div style={{ height: '60px', borderBottom: '2px solid #94a3b8', display: 'flex', alignItems: 'center', padding: '0 16px', gap: '12px' }}>
                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', border: '2px solid #94a3b8' }} />
                            <div style={{ width: '120px', height: '16px', display: 'flex', alignItems: 'center' }}>
                                <AnimatePresence mode="wait">
                                    <motion.span
                                        key={nameIndex}
                                        initial={{ y: 10, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        exit={{ y: -10, opacity: 0 }}
                                        style={{ fontSize: '16px', fontWeight: 700, color: '#475569' }}
                                    >
                                        {NAMES[nameIndex]}
                                    </motion.span>
                                </AnimatePresence>
                            </div>
                        </div>
                        {/* Wireframe Body */}
                        <div style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ width: '70%', height: '40px', border: '2px solid #cbd5e1', borderRadius: '12px 12px 12px 0' }} />
                            <div style={{ width: '60%', height: '40px', border: '2px solid #cbd5e1', borderRadius: '12px 12px 0 12px', alignSelf: 'flex-end' }} />
                        </div>
                    </div>
                    <div style={{ padding: '8px 16px', background: 'white', borderRadius: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', fontSize: '14px', fontWeight: 600, color: '#64748b' }}>
                        Define your persona
                    </div>
                </div>

                {/* STAGE 3: Styling (Headers) */}
                <div style={{ width: '25%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '24px' }}>
                    <div style={{
                        width: '300px', height: '400px',
                        border: '2px solid #94a3b8', borderRadius: '12px',
                        display: 'flex', flexDirection: 'column',
                        background: 'white', overflow: 'hidden'
                    }}>
                        {/* Changing Header */}
                        <motion.div
                            animate={{
                                background: HEADER_STYLES[styleIndex].bg,
                                borderBottom: HEADER_STYLES[styleIndex].border ? '2px solid #94a3b8' : 'none'
                            }}
                            style={{ height: '60px', display: 'flex', alignItems: 'center', padding: '0 16px', gap: '12px' }}
                        >
                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.5)' }} />
                            <div style={{ width: '100px', height: '12px', background: 'rgba(255,255,255,0.3)', borderRadius: '6px' }} />
                        </motion.div>
                        {/* Wireframe Body */}
                        <div style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ width: '70%', height: '40px', background: '#f1f5f9', borderRadius: '12px 12px 12px 0' }} />
                            <div style={{ width: '60%', height: '40px', background: '#e2e8f0', borderRadius: '12px 12px 0 12px', alignSelf: 'flex-end' }} />
                        </div>
                    </div>
                    <div style={{ padding: '8px 16px', background: 'white', borderRadius: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', fontSize: '14px', fontWeight: 600, color: '#64748b' }}>
                        Match your brand
                    </div>
                </div>

                {/* STAGE 4: Full Experience */}
                <div style={{ width: '25%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '24px' }}>
                    <motion.div
                        layout
                        style={{
                            width: '300px', height: '400px',
                            borderRadius: '16px',
                            display: 'flex', flexDirection: 'column',
                            background: 'white',
                            boxShadow: '0 20px 40px -10px rgba(0,0,0,0.15)',
                            overflow: 'hidden'
                        }}
                    >
                        {/* Full Header */}
                        <motion.div
                            animate={{ background: FULL_THEMES[themeIndex].color }}
                            style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}
                        >
                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'white', padding: '2px' }}>
                                <img src={`/assets/Avatars/AV${themeIndex + 10}.png`} alt="Bot" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                            </div>
                            <div>
                                <motion.div
                                    key={`t-${themeIndex}`}
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    style={{ color: 'white', fontWeight: 700, fontSize: '16px' }}
                                >
                                    {FULL_THEMES[themeIndex].name}
                                </motion.div>
                                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>Online</div>
                            </div>
                        </motion.div>

                        {/* Full Body */}
                        <div style={{ flex: 1, background: '#f8fafc', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <motion.div
                                key={`m-${themeIndex}`}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                style={{ alignSelf: 'flex-start', background: 'white', padding: '12px 16px', borderRadius: '12px 12px 12px 0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', maxWidth: '85%' }}
                            >
                                <p style={{ margin: 0, fontSize: '14px', color: '#334155', lineHeight: 1.5 }}>
                                    {FULL_THEMES[themeIndex].msg}
                                </p>
                            </motion.div>
                        </div>

                        {/* Input */}
                        <div style={{ padding: '16px', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                            <div style={{ height: '40px', background: '#f1f5f9', borderRadius: '20px', display: 'flex', alignItems: 'center', padding: '0 16px', color: '#94a3b8', fontSize: '13px' }}>
                                Type a message...
                            </div>
                        </div>
                    </motion.div>
                    <div style={{ padding: '8px 16px', background: 'white', borderRadius: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', fontSize: '14px', fontWeight: 600, color: '#64748b' }}>
                        Your unique assistant
                    </div>
                </div>

            </motion.div>

            {/* Progress Indicators */}
            <div style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '8px' }}>
                {[0, 1, 2, 3].map(i => (
                    <motion.div
                        key={i}
                        animate={{
                            width: stage === i ? 24 : 8,
                            background: stage === i ? '#3b82f6' : '#cbd5e1'
                        }}
                        style={{ height: '8px', borderRadius: '4px' }}
                    />
                ))}
            </div>
        </div>
    );
};

// 3. Lead Generation (Motion Graphic)

const TypingText = ({ text }: { text: string }) => {
    const [displayedText, setDisplayedText] = useState('');

    useEffect(() => {
        let i = 0;
        setDisplayedText('');
        const interval = setInterval(() => {
            setDisplayedText(text.slice(0, i + 1));
            i++;
            if (i > text.length) clearInterval(interval);
        }, 30); // Faster typing speed
        return () => clearInterval(interval);
    }, [text]);

    return <>{displayedText}</>;
};

const LeadGenVisual = () => {
    const [step, setStep] = useState(0);

    useEffect(() => {
        const sequence = async () => {
            while (true) {
                setStep(0); // Initial
                await new Promise(r => setTimeout(r, 800));
                setStep(1); // User: Hello!
                await new Promise(r => setTimeout(r, 1000));
                setStep(2); // Form Appears
                await new Promise(r => setTimeout(r, 1000));
                setStep(3); // Type Name
                await new Promise(r => setTimeout(r, 1500));
                setStep(4); // Type Email
                await new Promise(r => setTimeout(r, 1500));
                setStep(5); // Type Business URL
                await new Promise(r => setTimeout(r, 1500));
                setStep(6); // Click Submit
                await new Promise(r => setTimeout(r, 500));
                setStep(7); // Success (Sent!)
                await new Promise(r => setTimeout(r, 1500));
                setStep(8); // CRM View
                await new Promise(r => setTimeout(r, 4000));
            }
        };
        sequence();
    }, []);

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', position: 'relative' }}>

            <AnimatePresence mode="wait">
                {step < 8 ? (
                    <motion.div
                        key="chatbot"
                        exit={{ x: -50, opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        {/* Wireframe Chatbot Container */}
                        <div style={{
                            width: '350px',
                            height: '500px',
                            border: '2px solid #cbd5e1',
                            borderRadius: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            background: 'white',
                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)',
                            overflow: 'hidden',
                            position: 'relative'
                        }}>

                            {/* Wireframe Header */}
                            <div style={{
                                height: '60px',
                                borderBottom: '2px solid #cbd5e1',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '0 20px',
                                gap: '12px'
                            }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', border: '2px solid #cbd5e1' }} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <div style={{ width: '100px', height: '10px', background: '#e2e8f0', borderRadius: '5px' }} />
                                    <div style={{ width: '60px', height: '8px', background: '#f1f5f9', borderRadius: '4px' }} />
                                </div>
                            </div>

                            {/* Chat Area */}
                            <div style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px', position: 'relative' }}>

                                {/* User Message */}
                                <AnimatePresence>
                                    {step >= 1 && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10, scale: 0.9 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            style={{ alignSelf: 'flex-end', background: '#7c3aed', padding: '10px 16px', borderRadius: '16px 16px 0 16px', boxShadow: '0 4px 6px -1px rgba(124, 58, 237, 0.2)', maxWidth: '80%' }}
                                        >
                                            <p style={{ fontSize: '14px', color: 'white', margin: 0, fontWeight: 500 }}>I'm interested!</p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Form Card (Original Design) */}
                                <AnimatePresence>
                                    {step >= 2 && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '320px', background: '#334155', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
                                        >
                                            {step < 7 ? (
                                                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                                    <p style={{ fontSize: '14px', color: 'white', margin: 0, lineHeight: 1.5, fontWeight: 500 }}>
                                                        Would you like a personalized growth strategy sent right on your email?
                                                    </p>

                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                        {/* Name Input */}
                                                        <div style={{ position: 'relative' }}>
                                                            <div style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', fontSize: '14px', color: '#1e293b', height: '42px', display: 'flex', alignItems: 'center' }}>
                                                                {step >= 3 ? (
                                                                    <span style={{ color: '#1e293b' }}>
                                                                        {step === 3 ? <TypingText text="Sarah Jenkins" /> : "Sarah Jenkins"}
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ color: '#94a3b8' }}>Name *</span>
                                                                )}
                                                                {step === 3 && <motion.div animate={{ opacity: [1, 0] }} transition={{ repeat: Infinity, duration: 0.6 }} style={{ width: '2px', height: '16px', background: '#7c3aed', marginLeft: '2px' }} />}
                                                            </div>
                                                        </div>

                                                        {/* Email Input */}
                                                        <div style={{ position: 'relative' }}>
                                                            <div style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', fontSize: '14px', color: '#1e293b', height: '42px', display: 'flex', alignItems: 'center' }}>
                                                                {step >= 4 ? (
                                                                    <span style={{ color: '#1e293b' }}>
                                                                        {step === 4 ? <TypingText text="sarah@growth.com" /> : "sarah@growth.com"}
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ color: '#94a3b8' }}>Email *</span>
                                                                )}
                                                                {step === 4 && <motion.div animate={{ opacity: [1, 0] }} transition={{ repeat: Infinity, duration: 0.6 }} style={{ width: '2px', height: '16px', background: '#7c3aed', marginLeft: '2px' }} />}
                                                            </div>
                                                        </div>

                                                        {/* Business URL Input */}
                                                        <div style={{ position: 'relative' }}>
                                                            <div style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', fontSize: '14px', color: '#1e293b', height: '42px', display: 'flex', alignItems: 'center' }}>
                                                                {step >= 5 ? (
                                                                    <span style={{ color: '#1e293b' }}>
                                                                        {step === 5 ? <TypingText text="www.growthagency.com" /> : "www.growthagency.com"}
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ color: '#94a3b8' }}>Your business URL *</span>
                                                                )}
                                                                {step === 5 && <motion.div animate={{ opacity: [1, 0] }} transition={{ repeat: Infinity, duration: 0.6 }} style={{ width: '2px', height: '16px', background: '#7c3aed', marginLeft: '2px' }} />}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <motion.button
                                                        whileTap={{ scale: 0.95 }}
                                                        animate={step === 6 ? { scale: 0.95 } : {}}
                                                        style={{ background: '#7c3aed', color: 'white', border: 'none', padding: '12px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%' }}
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                                                        Submit
                                                    </motion.button>
                                                </div>
                                            ) : (
                                                <motion.div
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    style={{ padding: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', height: '240px' }}
                                                >
                                                    <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Check size={24} color="white" strokeWidth={3} />
                                                    </div>
                                                    <span style={{ fontSize: '16px', fontWeight: 600, color: 'white' }}>Sent!</span>
                                                </motion.div>
                                            )}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Simulated Cursor */}
                            <motion.div
                                animate={{
                                    x: step === 0 ? 0 : step === 4 ? 80 : step === 5 ? 80 : step === 6 ? 160 : 0,
                                    y: step === 0 ? 400 : step === 4 ? 240 : step === 5 ? 300 : step === 6 ? 360 : 400,
                                    opacity: [0, 1, 1, 1, 0]
                                }}
                                transition={{ duration: 0.5, ease: "easeInOut" }}
                                style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', zIndex: 50 }}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 3L10.07 19.97L12.58 12.58L19.97 10.07L3 3Z" fill="#1e293b" stroke="white" strokeWidth="2" strokeLinejoin="round" />
                                </svg>
                            </motion.div>

                            {/* Success Notification (Owner View) - REMOVED from here */}
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        key="crm"
                        initial={{ x: 50, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -50, opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        style={{ width: '100%', height: '100%', padding: '40px', display: 'flex', flexDirection: 'column', gap: '20px', position: 'relative' }}
                    >
                        {/* CRM Wireframe Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <div style={{ width: '120px', height: '24px', background: '#e2e8f0', borderRadius: '4px' }} />
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#e2e8f0' }} />
                            </div>
                        </div>

                        {/* CRM Table Header */}
                        <div style={{ display: 'flex', gap: '20px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
                            <div style={{ width: '25%', height: '12px', background: '#f1f5f9', borderRadius: '4px' }} />
                            <div style={{ width: '30%', height: '12px', background: '#f1f5f9', borderRadius: '4px' }} />
                            <div style={{ width: '25%', height: '12px', background: '#f1f5f9', borderRadius: '4px' }} />
                            <div style={{ width: '20%', height: '12px', background: '#f1f5f9', borderRadius: '4px' }} />
                        </div>

                        {/* CRM Rows */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {/* Row 1 (Placeholder) */}
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', opacity: 0.3 }}>
                                <div style={{ width: '25%', height: '16px', background: '#e2e8f0', borderRadius: '4px' }} />
                                <div style={{ width: '30%', height: '16px', background: '#e2e8f0', borderRadius: '4px' }} />
                                <div style={{ width: '25%', height: '16px', background: '#e2e8f0', borderRadius: '4px' }} />
                                <div style={{ width: '20%', height: '24px', background: '#e2e8f0', borderRadius: '12px' }} />
                            </div>

                            {/* Row 2 (The New Lead) */}
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0, background: '#f0f9ff' }}
                                animate={{ scale: 1, opacity: 1, background: 'white' }}
                                transition={{ delay: 0.3 }}
                                style={{ display: 'flex', gap: '20px', alignItems: 'center', padding: '12px', borderRadius: '8px', border: '1px solid #bfdbfe', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                            >
                                <div style={{ width: '25%', fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>Sarah Jenkins</div>
                                <div style={{ width: '30%', fontSize: '13px', color: '#64748b' }}>sarah@growth.com</div>
                                <div style={{ width: '25%', fontSize: '13px', color: '#64748b' }}>Growth Agency</div>
                                <div style={{ width: '20%' }}>
                                    <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>New Lead</span>
                                </div>
                            </motion.div>

                            {/* Row 3 (Placeholder) */}
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', opacity: 0.3 }}>
                                <div style={{ width: '25%', height: '16px', background: '#e2e8f0', borderRadius: '4px' }} />
                                <div style={{ width: '30%', height: '16px', background: '#e2e8f0', borderRadius: '4px' }} />
                                <div style={{ width: '25%', height: '16px', background: '#e2e8f0', borderRadius: '4px' }} />
                                <div style={{ width: '20%', height: '24px', background: '#e2e8f0', borderRadius: '12px' }} />
                            </div>
                        </div>

                        {/* Success Notification (Moved to CRM View) */}
                        <motion.div
                            initial={{ opacity: 0, y: -20, x: '-50%' }}
                            animate={{ opacity: 1, y: 20, x: '-50%' }}
                            transition={{ delay: 0.8 }}
                            style={{ position: 'absolute', top: '20px', left: '50%', background: '#1e293b', padding: '12px 20px', borderRadius: '30px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', gap: '12px', zIndex: 100, minWidth: '240px' }}
                        >
                            <div style={{ position: 'relative' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f472b6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: 600 }}>SJ</div>
                                <div style={{ position: 'absolute', bottom: 0, right: -2, width: '10px', height: '10px', background: '#10b981', borderRadius: '50%', border: '2px solid #1e293b' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>New Lead Captured</div>
                                <div style={{ fontSize: '11px', color: '#94a3b8' }}>Sarah Jenkins</div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// 4. Advanced Analytics (Dashboard.js replication)
const AnalyticsVisual = () => (
    <div style={{ width: '100%', height: '100%', background: '#f1f5f9', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: 0 }}>Dashboard</h3>
            <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 12px', fontSize: '13px', color: '#475569' }}>Today</div>
                <div style={{ background: '#e0e7ff', color: '#4338ca', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Premium</div>
            </div>
        </div>

        {/* Message Volume Trend */}
        <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', flex: '0 0 auto' }}>
            <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b', marginBottom: '16px' }}>Message Volume Trend</h4>
            <div style={{ height: '160px', position: 'relative', borderBottom: '1px solid #e2e8f0', borderLeft: '1px solid #e2e8f0' }}>
                {/* Grid lines */}
                {[0, 1, 2, 3, 4].map(i => (
                    <div key={i} style={{ position: 'absolute', bottom: `${i * 25}%`, left: 0, right: 0, borderTop: '1px dashed #f1f5f9' }} />
                ))}

                {/* Chart Line */}
                <svg style={{ width: '100%', height: '100%', overflow: 'visible' }} viewBox="0 0 100 100" preserveAspectRatio="none">
                    <path
                        d="M0,80 C15,80 20,30 35,30 S50,60 65,60 S80,40 85,40 S95,70 100,70"
                        fill="none"
                        stroke="#6366f1"
                        strokeWidth="2"
                        vectorEffect="non-scaling-stroke"
                    />
                    <path
                        d="M0,80 C15,80 20,30 35,30 S50,60 65,60 S80,40 85,40 S95,70 100,70 L100,100 L0,100 Z"
                        fill="url(#gradient)"
                        opacity="0.1"
                    />
                    <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#6366f1" />
                            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    {/* Points */}
                    {[
                        { x: 0, y: 80 }, { x: 35, y: 30 }, { x: 65, y: 60 }, { x: 85, y: 40 }, { x: 100, y: 70 }
                    ].map((p, i) => (
                        <circle key={i} cx={p.x} cy={p.y} r="1.5" fill="#6366f1" stroke="white" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                    ))}
                </svg>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '10px', color: '#94a3b8' }}>
                <span>12 AM</span>
                <span>6 AM</span>
                <span>12 PM</span>
                <span>6 PM</span>
                <span>11 PM</span>
            </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.2fr', gap: '16px', flex: 1 }}>
            {/* Most Common Topics */}
            <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b', margin: 0 }}>Most Common Topics</h4>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Volume</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {[
                        { name: 'Course Access and Progress', count: 28, color: '#10b981' },
                        { name: 'Assessment and Grading', count: 28, color: '#8b5cf6' },
                        { name: 'Certificate Issuance', count: 15, color: '#f59e0b' },
                        { name: 'Technical Issues', count: 13, color: '#ef4444' },
                        { name: 'Payment and Billing', count: 10, color: '#6366f1' }
                    ].map((topic, i) => (
                        <div key={i}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                                <span style={{ color: '#334155', fontWeight: 500 }}>{topic.name}</span>
                                <span style={{ color: '#1e293b', fontWeight: 600 }}>{topic.count}</span>
                            </div>
                            <div style={{ height: '6px', width: '100%', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${(topic.count / 28) * 100}%`, background: topic.color, borderRadius: '3px' }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Geographic Distribution - Pie Chart */}
            <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b', margin: 0 }}>Geographic</h4>
                    <Globe size={14} className="text-gray-400" />
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
                    {/* Pie Chart */}
                    <div style={{
                        width: '140px',
                        height: '140px',
                        borderRadius: '50%',
                        background: 'conic-gradient(#6366f1 0% 84%, #10b981 84% 89%, #f59e0b 89% 93.5%, #ef4444 93.5% 97%, #8b5cf6 97% 100%)',
                        position: 'relative'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '100px',
                            height: '100px',
                            background: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column'
                        }}>
                            <span style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>135</span>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>Users</span>
                        </div>
                    </div>

                    {/* Legend */}
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {[
                            { country: 'Ireland', color: '#6366f1', percent: '84%' },
                            { country: 'Vietnam', color: '#10b981', percent: '5%' },
                            { country: 'India', color: '#f59e0b', percent: '4%' },
                            { country: 'USA', color: '#ef4444', percent: '4%' },
                            { country: 'Oman', color: '#8b5cf6', percent: '3%' }
                        ].map((geo, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: geo.color }} />
                                    <span style={{ color: '#475569' }}>{geo.country}</span>
                                </div>
                                <span style={{ fontWeight: 600, color: '#1e293b' }}>{geo.percent}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const features = [
    {
        id: 'data',
        title: 'Train on your data',
        description: 'Upload PDFs, Word docs, or text files. Your chatbot learns directly from your knowledge base to provide accurate answers.',
        icon: Database,
        visual: FilesVisual
    },
    {
        id: 'appearance',
        title: 'Customizable Appearance',
        description: 'Match your brand identity. Choose from preset themes or customize colors, headers, and icons to fit your website perfectly.',
        icon: Palette,
        visual: CustomizationVisual
    },
    {
        id: 'lead-gen',
        title: 'Lead Generation',
        description: 'Create custom forms to collect user information like names, emails, and phone numbers before or during the chat.',
        icon: UserPlus,
        visual: LeadGenVisual
    },
    {
        id: 'analytics',
        title: 'Advanced Analytics',
        description: 'Track performance with a comprehensive dashboard. Monitor message volume, response times, and user satisfaction.',
        icon: BarChart3,
        visual: AnalyticsVisual
    }
];

const ProductShowcase: React.FC = () => {
    const [activeFeature, setActiveFeature] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);

    useEffect(() => {
        if (!isAutoPlaying) return;

        const interval = setInterval(() => {
            setActiveFeature((prev) => (prev + 1) % features.length);
        }, 12000);

        return () => clearInterval(interval);
    }, [isAutoPlaying]);

    const handleFeatureClick = (index: number) => {
        setActiveFeature(index);
        setIsAutoPlaying(false);
    };

    const ActiveVisual = features[activeFeature].visual;

    return (
        <Section>
            <Container>
                <TextContent>
                    <Header>
                        <Title>Everything you need to build a powerful AI agent</Title>
                        <Subtitle>
                            Create a custom ChatGPT for your website in minutes. No coding required.
                        </Subtitle>
                    </Header>
                    <FeatureList>
                        {features.map((feature, index) => (
                            <FeatureItem
                                key={feature.id}
                                isActive={activeFeature === index}
                                onClick={() => handleFeatureClick(index)}
                            >
                                <FeatureHeader>
                                    <div style={{
                                        padding: '10px',
                                        borderRadius: '12px',
                                        background: activeFeature === index
                                            ? 'linear-gradient(135deg, #8b5cf6, #3b82f6)'
                                            : 'rgba(243, 244, 246, 0.8)',
                                        color: activeFeature === index ? 'white' : '#6b7280',
                                        boxShadow: activeFeature === index ? '0 4px 12px rgba(139, 92, 246, 0.3)' : 'none',
                                        transition: 'all 0.3s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <feature.icon size={20} strokeWidth={activeFeature === index ? 2.5 : 2} />
                                    </div>
                                    <FeatureTitle isActive={activeFeature === index}>{feature.title}</FeatureTitle>
                                </FeatureHeader>
                                <FeatureDescription isActive={activeFeature === index}>
                                    {feature.description}
                                </FeatureDescription>
                                {activeFeature === index && isAutoPlaying && (
                                    <ProgressBar
                                        initial={{ scaleX: 0 }}
                                        animate={{ scaleX: 1 }}
                                        transition={{ duration: 12, ease: "linear" }}
                                    />
                                )}
                            </FeatureItem>
                        ))}
                    </FeatureList>
                </TextContent>
                <VisualContainer>
                    <AnimatePresence mode="wait">
                        <VisualContent
                            key={activeFeature}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <ActiveVisual />
                        </VisualContent>
                    </AnimatePresence>
                </VisualContainer>
            </Container>
        </Section>
    );
};

export default ProductShowcase;
